import { Trans, t } from '@lingui/macro'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
    Autocomplete,
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material'
import { Box } from '@mui/system'
import { CohortDTO, DeliveryCohortDTO, ExplorerCohortsService, PaginateMeta, RegistryType, RegistryTypeDTO } from '@om1/falcon-api'
import { FrameworkComponentProps } from '@om1/platform-utils'
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { dataDeliveryActions } from '../state/data-delivery'

export type DataDeliveryCohortInputProps = FrameworkComponentProps<
    {
        registryTypes: RegistryTypeDTO[]
        registryTypesLoading: boolean
    },
    Pick<typeof dataDeliveryActions, 'getDataDeliveryRegistryTypes'>,
    {
        value: DeliveryCohortDTO[]
        helperText?: string
        onChange: (cohorts: DeliveryCohortDTO[]) => void
        disabled: boolean
        datasets: CohortDTO[]
    }
>

interface CohortAutocompleteOption {
    label: string
    id: string
    isSystem: boolean
    cohortSize: number | null | undefined
}

const numberFormatter = new Intl.NumberFormat(undefined)

export const DataDeliveryCohortInputComponent = ({
    state: { registryTypes, registryTypesLoading },
    props: { value, helperText, onChange, disabled, datasets },
    actions
}: DataDeliveryCohortInputProps) => {
    const [selectedCohort, setSelectedCohort] = useState<CohortAutocompleteOption>()
    const [selectedRegistryType, setSelectedRegistryType] = useState<RegistryType | ''>('')
    const [sampleSizeType, setSampleSizeType] = useState<'all' | 'count' | 'percent'>('all')
    const [sampleCount, setSampleCount] = useState<number | ''>('')
    const [samplePercent, setSamplePercent] = useState<number | ''>('')
    const [query, setQuery] = useState<string>('')
    const [cohorts, setCohorts] = useState<CohortDTO[]>([])
    const [refMeta, setRefMeta] = useState<PaginateMeta>({
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10
    })
    const [loading, setLoading] = useState<boolean>(false)
    const [debouncedQuery, setDebouncedQuery] = useState<string>('')
    const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false)

    // Allow selecting the same cohort multiple times (e.g., different registry types)

    // Debounce logic using useEffect
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query)
        }, 300)

        return () => {
            clearTimeout(handler)
        }
    }, [query])

    // Fetch cohorts when debouncedQuery changes
    useEffect(() => {
        const fetchCohorts = async () => {
            try {
                setLoading(true)
                const response = await ExplorerCohortsService.indexExplorerCohortsGet({
                    query: debouncedQuery || undefined,
                    page: 1,
                    limit: refMeta.itemsPerPage
                })

                setCohorts(response.data) // Adjust based on API response
                setRefMeta(response.meta) // Update pagination info
            } catch (error) {
                // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                console.error('Failed to fetch cohorts:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchCohorts()
    }, [debouncedQuery, refMeta.itemsPerPage])

    // Fetch next page
    const fetchNextPage = async () => {
        if (isFetchingMore || refMeta.currentPage >= refMeta.totalPages) return

        setIsFetchingMore(true)
        try {
            const response = await ExplorerCohortsService.indexExplorerCohortsGet({
                query: debouncedQuery || undefined,
                page: refMeta.currentPage + 1,
                limit: refMeta.itemsPerPage
            })

            setCohorts((prev) => [...prev, ...response.data]) // Append new data
            setRefMeta(response.meta) // Update pagination info
        } catch (error) {
            // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
            console.error('Failed to fetch next page:', error)
        } finally {
            setIsFetchingMore(false)
        }
    }

    useEffect(() => {
        if (!registryTypesLoading && registryTypes.length === 0) {
            actions.getDataDeliveryRegistryTypes()
        }
    }, [actions, registryTypes, registryTypesLoading])

    const handleScroll = (event: React.SyntheticEvent) => {
        const listboxNode = event.currentTarget
        if (
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight - 10 // Adjust the threshold as needed
        ) {
            fetchNextPage()
        }
    }

    const handleAddCohort = () => {
        if (selectedCohort && selectedRegistryType) {
            // Validate inputs based on sample size type
            if (sampleSizeType === 'count' && (!sampleCount || !validateSampleCount(sampleCount, selectedCohort.cohortSize))) {
                return // Don't add if validation fails
            }

            if (sampleSizeType === 'percent' && (!samplePercent || !validateSamplePercent(samplePercent))) {
                return // Don't add if validation fails
            }

            // Prevent adding an exact duplicate (same cohort id + registry type)
            const isDuplicate = value.some((existing) => existing.cohort.id === selectedCohort.id && existing.registryType === selectedRegistryType)

            if (isDuplicate) {
                return
            }

            const dataDeliveryCohort: DeliveryCohortDTO = {
                id: '',
                cohort: {
                    id: selectedCohort.id,
                    name: selectedCohort.label,
                    cohortSize: selectedCohort.cohortSize
                },
                registryType: selectedRegistryType,
                sampleSize: calculateSampleSize()
            }

            onChange([...value, dataDeliveryCohort])

            // Reset all form fields
            setSelectedCohort(undefined)
            setSelectedRegistryType('')
            setSampleSizeType('all')
            setSampleCount('')
            setSamplePercent('')
        }
    }
    const handleRemoveCohort = useCallback(
        (cohort: DeliveryCohortDTO, index: number) => {
            const newValues = [...value.slice(0, index), ...value.slice(index + 1)]
            onChange(newValues)
        },
        [onChange, value]
    )

    const registryTypeLabelMap = useMemo(() => {
        return (registryTypes || []).reduce(
            (acc, reg) => ({
                ...acc,
                [reg.registryType]: reg.name
            }),
            {}
        )
    }, [registryTypes])

    const renderSelectedDataDeliveryCohorts = useCallback(() => {
        const label = (
            <Box marginBottom={0} marginTop={1}>
                <Trans>Selected Cohorts</Trans>
            </Box>
        )
        const selectedCohorts = value.map((d, index) => (
            <Box
                key={d.cohort.id}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                bgcolor='grey.200'
                fontSize={12}
                paddingX={1}
                paddingY={0.25}
            >
                <Box>
                    {d.cohort.name} ({getCohortSizeString(d.cohort.cohortSize!)}) - {registryTypeLabelMap[d.registryType]} -{' '}
                    {d.sampleSize === null || d.sampleSize === undefined ? t`All Patients` : `${d.sampleSize} ${t`Patients`}`}
                </Box>
                <IconButton color='primary' aria-label={t`Remove Cohort`} sx={{ padding: 0.5 }} onClick={() => handleRemoveCohort(d, index)}>
                    <DeleteOutlineIcon fontSize='small' />
                </IconButton>
            </Box>
        ))

        return (
            <>
                {value.length > 0 ? label : null}
                {selectedCohorts}
            </>
        )
    }, [value, registryTypeLabelMap, handleRemoveCohort])

    const cohortOptions: CohortAutocompleteOption[] = useMemo(() => {
        return datasets.concat(cohorts || []).map((c) => ({
            label: c.name,
            id: c.id,
            isSystem: c.isSystem,
            cohortSize: c.cohortSize
        }))
    }, [cohorts, datasets])

    const renderHelperText = () => {
        if (helperText) {
            return (
                <FormHelperText error sx={{ position: 'relative', bottom: 0 }}>
                    {helperText}
                </FormHelperText>
            )
        }

        return <></>
    }

    const renderRegistryTypeOptions = useMemo(() => {
        return (registryTypes || []).map((registry) => (
            <MenuItem value={registry.registryType} key={registry.registryType}>
                {registry.name}
            </MenuItem>
        ))
    }, [registryTypes])

    const renderSampleSizeOptions = () => {
        const sampleSizeOptions = [
            { value: 'all', label: t`All Patients` },
            { value: 'count', label: t`Sample Count` },
            { value: 'percent', label: t`Sample Percent` }
        ]

        return sampleSizeOptions.map((option) => (
            <MenuItem value={option.value} key={option.value}>
                {option.label}
            </MenuItem>
        ))
    }

    const getCohortSizeString = (cohortSize: number | null | undefined): string => {
        return typeof cohortSize === 'number' ? numberFormatter.format(cohortSize) : '—'
    }

    const validateSampleCount = (count: number, cohortSize: number | null | undefined): boolean => {
        return cohortSize !== null && cohortSize !== undefined && count > 0 && count < cohortSize
    }

    const validateSamplePercent = (percent: number): boolean => {
        return percent >= 1 && percent <= 99
    }

    const calculateSampleSize = (): number | null => {
        if (sampleSizeType === 'all') {
            return null // null represents "all patients"
        }

        if (sampleSizeType === 'count' && typeof sampleCount === 'number') {
            return sampleCount
        }

        if (sampleSizeType === 'percent' && typeof samplePercent === 'number' && selectedCohort?.cohortSize) {
            const calculatedSize = Math.round((samplePercent / 100) * selectedCohort.cohortSize)
            return calculatedSize
        }

        return null
    }

    return (
        <>
            <Box>
                <Box display='flex' gap={2} alignItems='center'>
                    <FormControl sx={{ flex: '2 0' }}>
                        <Autocomplete
                            blurOnSelect
                            disableClearable
                            disabled={disabled}
                            options={cohortOptions}
                            groupBy={(option: CohortAutocompleteOption) => (option.isSystem ? t`Datasets` : t`Custom Cohorts`)}
                            getOptionLabel={(option) => `${option.label} (${getCohortSizeString(option.cohortSize)})`}
                            isOptionEqualToValue={(option, value) => {
                                return value && value.id === option.id
                            }}
                            loading={loading}
                            // @ts-ignore null isn't correct from a type perspective, but undefined causes uncontrolled -> controlled errors
                            value={
                                selectedCohort
                                    ? {
                                          label: selectedCohort.label,
                                          id: selectedCohort.id,
                                          isSystem: selectedCohort.isSystem,
                                          cohortSize: selectedCohort.cohortSize || 0
                                      }
                                    : null
                            }
                            onChange={(event: SyntheticEvent<Element, Event>, value: NonNullable<CohortAutocompleteOption>) => {
                                setSelectedCohort(value)
                                // Reset dependent fields when cohort changes
                                setSelectedRegistryType('')
                                setSampleSizeType('all')
                                setSampleCount('')
                                setSamplePercent('')
                            }}
                            onInputChange={(event, newInputValue) => {
                                setQuery(newInputValue)
                            }}
                            ListboxProps={{
                                onScroll: handleScroll
                            }}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.id}>
                                        {option.label} ({getCohortSizeString(option.cohortSize)})
                                    </li>
                                )
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t`Cohort`}
                                    InputProps={{
                                        ...params.InputProps,
                                        type: 'search'
                                    }}
                                />
                            )}
                            loadingText={<CircularProgress size={20} />}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: '1 0' }}>
                        <InputLabel id='type-label'>
                            <Trans>Type</Trans>
                        </InputLabel>
                        <Select
                            id='type'
                            labelId='type-label'
                            label={<Trans>Type</Trans>}
                            disabled={disabled || !selectedCohort}
                            value={selectedRegistryType}
                            onChange={(event) => {
                                setSelectedRegistryType(event.target.value as RegistryType)
                            }}
                        >
                            {renderRegistryTypeOptions}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: '1 0' }}>
                        <InputLabel id='size-label'>
                            <Trans>Size</Trans>
                        </InputLabel>
                        <Select
                            id='size'
                            labelId='size-label'
                            label={<Trans>Size</Trans>}
                            disabled={disabled || !selectedCohort}
                            value={sampleSizeType}
                            onChange={(event) => {
                                setSampleSizeType(event.target.value as 'all' | 'count' | 'percent')
                                setSampleCount('')
                                setSamplePercent('')
                            }}
                        >
                            {renderSampleSizeOptions()}
                        </Select>
                    </FormControl>

                    {/* Conditional input fields for sample count and percent */}
                    {sampleSizeType === 'count' && (
                        <FormControl sx={{ flex: '1 0' }}>
                            <TextField
                                label={t`Sample Count`}
                                type='number'
                                inputProps={{ min: 1 }}
                                value={sampleCount}
                                onChange={(event) => {
                                    const value = event.target.value === '' ? '' : parseInt(event.target.value, 10)
                                    setSampleCount(value)
                                }}
                                error={typeof sampleCount === 'number' && !validateSampleCount(sampleCount, selectedCohort?.cohortSize)}
                                helperText={
                                    typeof sampleCount === 'number' && !validateSampleCount(sampleCount, selectedCohort?.cohortSize)
                                        ? t`Must be less than ${getCohortSizeString(selectedCohort?.cohortSize)}`
                                        : ''
                                }
                                disabled={disabled || !selectedCohort}
                            />
                        </FormControl>
                    )}

                    {sampleSizeType === 'percent' && (
                        <FormControl sx={{ flex: '1 0' }}>
                            <TextField
                                label={t`Sample Percent`}
                                type='number'
                                value={samplePercent}
                                onChange={(event) => {
                                    const value = event.target.value === '' ? '' : parseInt(event.target.value, 10)
                                    setSamplePercent(value)
                                }}
                                error={typeof samplePercent === 'number' && !validateSamplePercent(samplePercent)}
                                helperText={
                                    typeof samplePercent === 'number' && !validateSamplePercent(samplePercent) ? t`Must be between 1 - 99` : ''
                                }
                                disabled={disabled || !selectedCohort}
                                inputProps={{ min: 1, max: 99 }}
                            />
                        </FormControl>
                    )}
                    <Box flex='0 0'>
                        <Button
                            sx={{ paddingY: '0.25rem' }}
                            startIcon={<AddIcon />}
                            onClick={handleAddCohort}
                            disabled={
                                !selectedCohort ||
                                !selectedRegistryType ||
                                (sampleSizeType === 'count' && (!sampleCount || !validateSampleCount(sampleCount, selectedCohort.cohortSize))) ||
                                (sampleSizeType === 'percent' && (!samplePercent || !validateSamplePercent(samplePercent)))
                            }
                        >
                            <Trans>Add</Trans>
                        </Button>
                    </Box>
                </Box>
                <Box display='flex' flexDirection='column' gap={1}>
                    {renderSelectedDataDeliveryCohorts()}
                    {renderHelperText()}
                </Box>
            </Box>
        </>
    )
}
